/**
 * Mirrors `services/engine/src/api/routes/flows.ts` — the flow catalogue, its
 * graph, and its run log.
 *
 *   GET    /pipelines/:pid/flows                    → { items: FlowSummary[] }
 *   POST   /pipelines/:pid/flows                    → { flow } (201)
 *   GET    /pipelines/:pid/flows/:id                → { flow, triggers }
 *   PUT    /pipelines/:pid/flows/:id/graph          → { flow } | 400 | 409
 *   PATCH  /pipelines/:pid/flows/:id                → { flow } | 400 | 409
 *   DELETE /pipelines/:pid/flows/:id                → 204
 *   GET    /pipelines/:pid/flows/:id/runs           → { items, nextCursor }
 *   GET    /pipelines/:pid/flows/:id/runs/:runId    → { run }
 *
 * Same posture as `pipelines.ts` and `sources.ts` — Zod at the network
 * boundary, `cast*` helpers, one typed error instead of an axios shape. Two
 * things differ, and both come from the engine side:
 *
 * - **Timestamps arrive as epoch millis, not ISO strings.** A run's dates are
 *   projected through `.getTime()` by `flows/flows.ts` (`toRunSummary`); a
 *   step's are already millis in the stored JSON, written that way by
 *   `flows/walk.ts` and passed through raw by `getRun`. Either way these
 *   schemas read numbers and **every** cast turns them into `Date` — the shape
 *   the rest of the console already sees from `pipelines.ts`. One response
 *   never carries two timestamp conventions.
 * - **A refusal carries a machine-readable body the console must not flatten.**
 *   A rejected save answers `{ error: 'invalid_graph', issues: FlowIssue[] }`
 *   and a rejected enable answers `{ error: 'flow_invalid', reasons: string[] }`.
 *   An entry that is about one node carries that node's id, which is what lets
 *   a mark be drawn where the problem is instead of one message over the whole
 *   graph — the route says so itself. {@link FlowApiError} therefore carries
 *   both bodies through rather than collapsing them to a string.
 */

import { z } from 'zod'
import { asPipelineId, type FlowGraph, type FlowIssue, type PipelineId } from '@ludus/core'
import { apiClient } from './client.ts'

// ──────────────────────────────────────────────────────────────────────────────
// Schemas
// ──────────────────────────────────────────────────────────────────────────────

const flowSummarySchema = z.object({
  id: z.string(),
  pipelineId: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  version: z.number(),
  createdAt: z.number(),
  updatedAt: z.number(),
})

/**
 * The stored graph, checked for shape only.
 *
 * `type` is read as a plain string although `FlowNode.type` is a closed union,
 * for the reason `sources.ts` reads a discovery field's `kind` as one: this
 * blob was written by some console build against some registry, and refusing
 * the whole payload over a single node this build does not recognise would
 * blank an editor the operator opened to fix exactly that. Core owns the
 * narrowing — `isKnownNodeType` — and its validator has an `unknown_node_type`
 * issue for the case, so nothing downstream has to trust the cast below.
 */
const nodeSchema = z.object({
  id: z.string(),
  key: z.string(),
  type: z.string(),
  options: z.record(z.unknown()),
  resolve: z.string().nullable(),
  reject: z.string().nullable(),
  position: z.object({ x: z.number(), y: z.number() }),
})

const graphSchema = z.object({
  nodes: z.array(nodeSchema),
  retiredKeys: z.array(z.string()).optional(),
})

const flowDetailSchema = flowSummarySchema.extend({ graph: graphSchema })

const triggerSchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  eventType: z.string(),
  match: z.unknown(),
  enabled: z.boolean(),
})

const RUN_STATES = ['running', 'done', 'failed'] as const

const runSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  state: z.enum(RUN_STATES),
  attempts: z.number(),
  triggerEventId: z.string(),
  triggerSeq: z.number(),
  correlationId: z.string().nullable(),
  graphVersion: z.number(),
  error: z.string().nullable(),
  startedAt: z.number(),
  finishedAt: z.number().nullable(),
  createdAt: z.number(),
})

/**
 * One entry of `flow_runs.steps`, mirroring the engine's `StepRecord`
 * (`services/engine/src/flows/nodes/context.ts`).
 *
 * `status` is read as a plain string for the same reason a node's `type` is: a
 * step log is history, and a run written by a build with a fifth outcome must
 * still be readable here.
 */
const stepSchema = z.object({
  nodeId: z.string(),
  key: z.string(),
  status: z.string(),
  renderedOptions: z.record(z.unknown()),
  output: z.unknown(),
  reason: z.string().optional(),
  startedAt: z.number(),
  finishedAt: z.number(),
})

const runsPageSchema = z.object({
  items: z.array(runSchema),
  nextCursor: z.string().nullable(),
})

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

/** A row of the catalogue. Deliberately without the graph — the engine's big column. */
export interface FlowSummary {
  id: string
  pipelineId: PipelineId
  name: string
  enabled: boolean
  version: number
  createdAt: Date
  updatedAt: Date
}

export interface FlowDetail extends FlowSummary {
  graph: FlowGraph
}

/** One `flow_triggers` row as the engine projected it from the graph. */
export interface FlowTrigger {
  id: string
  nodeId: string
  eventType: string
  match: unknown
  enabled: boolean
}

export type FlowRunState = (typeof RUN_STATES)[number]

export interface FlowRun {
  id: string
  flowId: string
  state: FlowRunState
  attempts: number
  triggerEventId: string
  triggerSeq: number
  correlationId: string | null
  graphVersion: number
  /** Machine-readable, and `null` on the happy path. */
  error: string | null
  startedAt: Date
  finishedAt: Date | null
  createdAt: Date
}

export interface FlowRunStep {
  nodeId: string
  key: string
  status: string
  renderedOptions: Record<string, unknown>
  output: unknown
  reason?: string
  startedAt: Date
  finishedAt: Date
}

export interface FlowRunDetail extends FlowRun {
  steps: FlowRunStep[]
}

export interface FlowRunsPage {
  items: FlowRun[]
  nextCursor: string | null
}

// ──────────────────────────────────────────────────────────────────────────────
// Errors
// ──────────────────────────────────────────────────────────────────────────────

export class FlowApiError extends Error {
  readonly kind:
    | 'unauthorized'
    | 'not_found'
    | 'invalid_input'
    | 'invalid_graph'
    | 'conflict'
    | 'flow_invalid'
    | 'malformed_response'
    | 'network'
    | 'unknown'
  readonly status: number | null
  /** The engine's own words on `invalid_input` — never a status code. */
  readonly detail: string | null
  /** Why the graph was refused at save, per node. Empty unless `invalid_graph`. */
  readonly issues: FlowIssue[]
  /** Why the flow was refused at enable, per node. Empty unless `flow_invalid`. */
  readonly reasons: string[]

  constructor(args: {
    kind: FlowApiError['kind']
    status: number | null
    message: string
    detail?: string | null
    issues?: FlowIssue[]
    reasons?: string[]
  }) {
    super(args.message)
    this.name = 'FlowApiError'
    this.kind = args.kind
    this.status = args.status
    this.detail = args.detail ?? null
    this.issues = args.issues ?? []
    this.reasons = args.reasons ?? []
  }
}

interface EngineErrorBody {
  error?: string
  reason?: string
  issues?: unknown
  reasons?: unknown
}

const asIssues = (value: unknown): FlowIssue[] =>
  Array.isArray(value) ? (value as FlowIssue[]) : []

const asReasons = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((r): r is string => typeof r === 'string') : []

export function classifyFlowError(err: unknown): FlowApiError {
  // Before the transport branch, because every fetcher parses inside its own
  // `try`: a response the engine answered but this build cannot read has no
  // `.response` on the thrown Zod error, so the `status === null` test below
  // would claim it and the operator would be told to check a connection that
  // is working. A schema mismatch is a contract failure between two deploys,
  // and it says so.
  if (err instanceof z.ZodError) {
    return new FlowApiError({
      kind: 'malformed_response',
      status: null,
      message: 'malformed_response',
    })
  }

  const e = err as { response?: { status?: number; data?: EngineErrorBody }; message?: string }
  const status = e.response?.status ?? null
  const body = e.response?.data ?? {}

  if (status === null) {
    return new FlowApiError({ kind: 'network', status, message: e.message ?? 'network error' })
  }
  if (status === 401) {
    return new FlowApiError({ kind: 'unauthorized', status, message: 'unauthorized' })
  }
  if (status === 404) {
    return new FlowApiError({ kind: 'not_found', status, message: 'not_found' })
  }
  if (status === 400 && body.error === 'invalid_graph') {
    return new FlowApiError({
      kind: 'invalid_graph',
      status,
      message: 'invalid_graph',
      issues: asIssues(body.issues),
    })
  }
  if (status === 400) {
    return new FlowApiError({
      kind: 'invalid_input',
      status,
      message: 'invalid_input',
      detail: body.reason ?? null,
    })
  }
  // Two different 409s: a save or an enable that raced another writer
  // (`version_conflict`), and an enable the world no longer allows
  // (`flow_invalid` — a retired target status, an owner off the board).
  if (status === 409 && body.error === 'flow_invalid') {
    return new FlowApiError({
      kind: 'flow_invalid',
      status,
      message: 'flow_invalid',
      reasons: asReasons(body.reasons),
    })
  }
  if (status === 409) {
    return new FlowApiError({ kind: 'conflict', status, message: 'version_conflict' })
  }
  return new FlowApiError({ kind: 'unknown', status, message: e.message ?? `http ${status}` })
}

// ──────────────────────────────────────────────────────────────────────────────
// Casts
// ──────────────────────────────────────────────────────────────────────────────

const castSummary = (parsed: z.infer<typeof flowSummarySchema>): FlowSummary => ({
  ...parsed,
  pipelineId: asPipelineId(parsed.pipelineId),
  createdAt: new Date(parsed.createdAt),
  updatedAt: new Date(parsed.updatedAt),
})

const castDetail = (parsed: z.infer<typeof flowDetailSchema>): FlowDetail => ({
  ...castSummary(parsed),
  graph: parsed.graph as FlowGraph,
})

const castRun = (parsed: z.infer<typeof runSchema>): FlowRun => ({
  ...parsed,
  startedAt: new Date(parsed.startedAt),
  finishedAt: parsed.finishedAt === null ? null : new Date(parsed.finishedAt),
  createdAt: new Date(parsed.createdAt),
})

// `match` and `output` are spelled out rather than spread: Zod infers a key
// typed `unknown` as optional, and both are always present on the wire.
const castTrigger = (parsed: z.infer<typeof triggerSchema>): FlowTrigger => ({
  id: parsed.id,
  nodeId: parsed.nodeId,
  eventType: parsed.eventType,
  match: parsed.match,
  enabled: parsed.enabled,
})

const castStep = (parsed: z.infer<typeof stepSchema>): FlowRunStep => ({
  nodeId: parsed.nodeId,
  key: parsed.key,
  status: parsed.status,
  renderedOptions: parsed.renderedOptions,
  output: parsed.output,
  reason: parsed.reason,
  startedAt: new Date(parsed.startedAt),
  finishedAt: new Date(parsed.finishedAt),
})

// ──────────────────────────────────────────────────────────────────────────────
// Paths
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Every id is encoded on its way into a path, and the two builders below are
 * where that happens so a ninth fetcher cannot forget it.
 *
 * `flowId` and `runId` reach these functions from route params, and React
 * Router hands a param back percent-*decoded* — so a `%3F` in the address bar
 * arrives here as a literal `?`, and interpolating it raw would turn the rest
 * of the path into a query string and address a different endpoint than the
 * one the caller named. Encoding keeps a value one segment, or makes the
 * request fail honestly. `lead-detail.ts` does the same for `mediaId`.
 */
const flowsPath = (pipelineId: PipelineId): string =>
  `/pipelines/${encodeURIComponent(pipelineId)}/flows`

const flowPath = (pipelineId: PipelineId, flowId: string): string =>
  `${flowsPath(pipelineId)}/${encodeURIComponent(flowId)}`

// ──────────────────────────────────────────────────────────────────────────────
// Fetchers
// ──────────────────────────────────────────────────────────────────────────────

export async function listFlows(pipelineId: PipelineId): Promise<FlowSummary[]> {
  try {
    const { data } = await apiClient.get(flowsPath(pipelineId))
    return z
      .object({ items: z.array(flowSummarySchema) })
      .parse(data)
      .items.map(castSummary)
  } catch (err) {
    throw classifyFlowError(err)
  }
}

export async function fetchFlow(
  pipelineId: PipelineId,
  flowId: string,
): Promise<{ flow: FlowDetail; triggers: FlowTrigger[] }> {
  try {
    const { data } = await apiClient.get(flowPath(pipelineId, flowId))
    const parsed = z
      .object({ flow: flowDetailSchema, triggers: z.array(triggerSchema) })
      .parse(data)
    return { flow: castDetail(parsed.flow), triggers: parsed.triggers.map(castTrigger) }
  } catch (err) {
    throw classifyFlowError(err)
  }
}

/**
 * A new flow is born disabled, with one unconfigured trigger node — the draft
 * `newFlowGraph()` mints. The caller navigates to the canvas to finish it.
 */
export async function createFlow(
  pipelineId: PipelineId,
  input: { name: string },
): Promise<FlowSummary> {
  try {
    const { data } = await apiClient.post(flowsPath(pipelineId), input)
    return castSummary(z.object({ flow: flowSummarySchema }).parse(data).flow)
  } catch (err) {
    throw classifyFlowError(err)
  }
}

/**
 * Whole-graph save. `version` is the compare-and-swap: hand back the version
 * that came with the graph being edited, and a `conflict` means someone else
 * saved in between — re-read rather than retry with the same value.
 */
export async function saveFlowGraph(
  pipelineId: PipelineId,
  flowId: string,
  input: { graph: FlowGraph; version: number },
): Promise<FlowSummary> {
  try {
    const { data } = await apiClient.put(`${flowPath(pipelineId, flowId)}/graph`, input)
    return castSummary(z.object({ flow: flowSummarySchema }).parse(data).flow)
  } catch (err) {
    throw classifyFlowError(err)
  }
}

/** Enable is not Save (ADR-0007 §2): turning a flow on is its own act. */
export async function setFlowEnabled(
  pipelineId: PipelineId,
  flowId: string,
  enabled: boolean,
): Promise<FlowSummary> {
  try {
    const { data } = await apiClient.patch(flowPath(pipelineId, flowId), { enabled })
    return castSummary(z.object({ flow: flowSummarySchema }).parse(data).flow)
  } catch (err) {
    throw classifyFlowError(err)
  }
}

export async function deleteFlow(pipelineId: PipelineId, flowId: string): Promise<void> {
  try {
    await apiClient.delete(flowPath(pipelineId, flowId))
  } catch (err) {
    throw classifyFlowError(err)
  }
}

/**
 * One page of a flow's runs, newest first. The cursor is opaque and keyset —
 * a token the engine minted, never a page number, and a malformed one is a
 * refusal rather than a silent restart at page one.
 */
export async function listFlowRuns(
  pipelineId: PipelineId,
  flowId: string,
  query: { limit?: number; cursor?: string | null } = {},
): Promise<FlowRunsPage> {
  try {
    const { data } = await apiClient.get(`${flowPath(pipelineId, flowId)}/runs`, {
      params: {
        ...(query.limit === undefined ? {} : { limit: query.limit }),
        ...(query.cursor ? { cursor: query.cursor } : {}),
      },
    })
    const parsed = runsPageSchema.parse(data)
    return { items: parsed.items.map(castRun), nextCursor: parsed.nextCursor }
  } catch (err) {
    throw classifyFlowError(err)
  }
}

export async function fetchFlowRun(
  pipelineId: PipelineId,
  flowId: string,
  runId: string,
): Promise<FlowRunDetail> {
  try {
    const { data } = await apiClient.get(
      `${flowPath(pipelineId, flowId)}/runs/${encodeURIComponent(runId)}`,
    )
    const parsed = z
      .object({ run: runSchema.extend({ steps: z.array(stepSchema) }) })
      .parse(data).run
    return { ...castRun(parsed), steps: parsed.steps.map(castStep) }
  } catch (err) {
    throw classifyFlowError(err)
  }
}
